/**
 * Query Key Factory
 * Centralized query key generation following TanStack Query best practices
 * Ensures consistent, type-safe query keys across all hooks
 */

import type { CalendarDataParams, OpenSlotsParams } from "./types";

/**
 * Query key factory object
 * Provides a structured way to generate query keys for all API operations
 */
export const queryKeys = {
  // ========================================================================
  // CALENDAR QUERY KEYS
  // ========================================================================
  calendar: {
    all: () => ["calendar"] as const,
    google: () => ["google-calendar"] as const,
    googleWithParams: (params?: CalendarDataParams) =>
      ["google-calendar", params] as const,
    microsoft: () => ["microsoft-calendar"] as const,
    microsoftWithParams: (params?: CalendarDataParams) =>
      ["microsoft-calendar", params] as const,
    combined: () => ["combined-calendar"] as const,
    combinedWithParams: (params?: CalendarDataParams) =>
      ["combined-calendar", params] as const,
    externalGoogle: () => ["external-google-events"] as const,
    externalGoogleWithParams: (params?: CalendarDataParams) =>
      ["external-google-events", params] as const,
    externalMicrosoft: () => ["external-microsoft-events"] as const,
    externalMicrosoftWithParams: (params?: CalendarDataParams) =>
      ["external-microsoft-events", params] as const,
  },

  // ========================================================================
  // BOOKING QUERY KEYS
  // ========================================================================
  booking: {
    all: () => ["booking"] as const,
    openSlots: () => ["open-slots"] as const,
    openSlotsWithParams: (
      bookeeEmail: string,
      selectedWeek: Date,
      meetingDuration: number
    ) => ["open-slots", bookeeEmail, selectedWeek, meetingDuration] as const,
  },

  // ========================================================================
  // ACCOUNT QUERY KEYS
  // ========================================================================
  account: {
    all: () => ["account"] as const,
    status: () => ["account-status"] as const,
  },

  // ========================================================================
  // CRM QUERY KEYS
  // ========================================================================
  crm: {
    all: () => ["crm"] as const,
    search: () => ["crm-search"] as const,
  },
};

/**
 * Helper function to invalidate all calendar-related queries
 * Useful when calendar data changes (e.g., after creating/deleting an event)
 */
export const getCalendarQueryKeysToInvalidate = () => [
  queryKeys.calendar.google(),
  queryKeys.calendar.microsoft(),
  queryKeys.calendar.combined(),
  queryKeys.calendar.externalGoogle(),
  queryKeys.calendar.externalMicrosoft(),
];

/**
 * Helper function to get all query keys that should be invalidated
 * when account status changes (e.g., after connecting/disconnecting a calendar)
 */
export const getAccountRelatedQueryKeysToInvalidate = () => [
  queryKeys.account.status(),
  ...getCalendarQueryKeysToInvalidate(),
  queryKeys.booking.openSlots(),
];
